import { useTranslation } from 'react-i18next';
import Checklist from '../../common/Checklist/Checklist';
import commonStyles from '../common.module.css';
import styles from './ServiceInformationRegister.module.css';
import Customer from '../../../models/Customer';

type ServiceInformationProps = {
  customer: Customer
  onUpdate: (newCustomer: Customer) => void
}

function ServiceInformationRegister(props: ServiceInformationProps) {
  const { t } = useTranslation();

  const serviceChecklistLabel = [
    {
      id: 'FINANCIAL_ADVISORY',
      name: t('customer.serviceInfo.checklist.service.financialAdvisory'),
      isChecked: props.customer.servicesProvided.includes('FINANCIAL_ADVISORY'),
    },
    {
      id: 'WEALTH_MANAGEMENT',
      name: t('customer.serviceInfo.checklist.service.wealthManagement'),
      isChecked: props.customer.servicesProvided.includes('WEALTH_MANAGEMENT'),
    },
  ];

  const questionnaireChecklistLabel = [
    {
      id: 'KYC_FINANCIAL_ADVISORY',
      name: t('customer.serviceInfo.checklist.questionnaire.kycFinancialAdvisory'),
      isChecked: props.customer.questionnaireTypesRequired.includes('KYC_FINANCIAL_ADVISORY'),
    },
    {
      id: 'KYC_WEALTH_MANAGEMENT',
      name: t('customer.serviceInfo.checklist.questionnaire.kycWealthManagement'),
      isChecked: props.customer.questionnaireTypesRequired.includes('KYC_WEALTH_MANAGEMENT'),
    },
    {
      id: 'MIFID_CLIENT_CATEGORISATION',
      name: t('customer.serviceInfo.checklist.questionnaire.mifidClientCategorisation'),
      isChecked: props.customer.questionnaireTypesRequired.includes('MIFID_CLIENT_CATEGORISATION'),
    },
    {
      id: 'MIFID_APPROPRIATENESS',
      name: t('customer.serviceInfo.checklist.questionnaire.mifidAppropriateness'),
      isChecked: props.customer.questionnaireTypesRequired.includes('MIFID_APPROPRIATENESS'),
    },
    {
      id: 'MIFID_SUITABILITY',
      name: t('customer.serviceInfo.checklist.questionnaire.mifidSuitability'),
      isChecked: props.customer.questionnaireTypesRequired.includes('MIFID_SUITABILITY'),
    },
  ];

  const addValue = (checklist: string[], value: string): string[] => checklist.concat(value);
  const removeValue = (checklist: string[], value: string): string[] => checklist.filter(
    item => item !== value,
  );

  const serviceChecklistUpdate = (value: string, isChecked: boolean) => {
    if (value === 'FINANCIAL_ADVISORY') {
      props.onUpdate({
        ...props.customer, ...(isChecked
          ? {
            servicesProvided: addValue(props.customer.servicesProvided, value),
            questionnaireTypesRequired: addValue(
              props.customer.questionnaireTypesRequired, 'KYC_FINANCIAL_ADVISORY',
            ),
          }
          : {
            servicesProvided: removeValue(props.customer.servicesProvided, value),
            questionnaireTypesRequired: removeValue(
              props.customer.questionnaireTypesRequired, 'KYC_FINANCIAL_ADVISORY',
            ),
          }),
      });
    }
    if (value === 'WEALTH_MANAGEMENT') {
      props.onUpdate({
        ...props.customer, ...(isChecked
          ? {
            servicesProvided: addValue(props.customer.servicesProvided, value),
            questionnaireTypesRequired: props.customer.questionnaireTypesRequired.concat(
              'KYC_WEALTH_MANAGEMENT', 'MIFID_CLIENT_CATEGORISATION',
            ),
          }
          : {
            servicesProvided: removeValue(props.customer.servicesProvided, value),
            questionnaireTypesRequired: removeValue(
              props.customer.questionnaireTypesRequired, 
              'KYC_WEALTH_MANAGEMENT',
            ),
          }),
      });
    }
  };

  return (
    <div className={commonStyles.container}>
      <div className={styles.content}>
        <div className={commonStyles.checklist}>
          <Checklist
            id='service'
            label={t('customer.serviceInfo.label.firstServiceInfoQuestion')}
            checklist={serviceChecklistLabel}
            onUpdate={(value, isChecked) => serviceChecklistUpdate(value, isChecked)}
          />
        </div>
        <div className={commonStyles.checklist}>
          <Checklist
            id='questionnaire'
            label={t('customer.serviceInfo.label.secondServiceInfoQuestion')}
            checklist={questionnaireChecklistLabel}
            onUpdate={(value, isChecked) => props.onUpdate({
              ...props.customer,
              ...(isChecked
                ? {
                  questionnaireTypesRequired: addValue(
                    props.customer.questionnaireTypesRequired, value,
                  ),
                }
                : {
                  questionnaireTypesRequired: removeValue(
                    props.customer.questionnaireTypesRequired, value,
                  ),
                }),
            })}
          />
        </div>
      </div>
    </div>
  );
}

export default ServiceInformationRegister;
